import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import { Plus, Minus, Save, ArrowLeft } from 'lucide-react';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import { validateForm } from '../utils/validation';
import { useAuth } from '../context/AuthContext';

const InvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth(); // Get current user from auth context
  const isEditing = Boolean(id);
  const prefilledCustomer = location.state?.prefilledCustomer;
  const fromEstimate = location.state?.fromEstimate;

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    salesperson_id: '',
    customer: prefilledCustomer || {
      name: '',
      phone: '',
      email: '',
      address: '',
      gstNumber: ''
    }
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [formTouched, setFormTouched] = useState(false);

  const [items, setItems] = useState([{
    category_id: '',
    product_id: '',
    quantity: 1,
    unit_price: 0,
    tax: 0,
    discount: 0,
    subtotal: 0
  }]);

  useEffect(() => {
    fetchInitialData();
    if (isEditing) {
      loadInvoiceData();
    } else if (fromEstimate) {
      loadEstimateData();
    }
  }, [isEditing, fromEstimate]);

  const fetchInitialData = async () => {
    try {
      console.log('🔄 Invoice - Fetching initial data (categories, salespersons)...');
      console.log('👤 Invoice - Current user:', user); // Debug current user

      const [categoriesRes, salespersonsRes] = await Promise.all([
        axios.get('/sales/categories'),
        axios.get('/sales/salespersons')
      ]);

      console.log('📂 Invoice - Categories response:', categoriesRes.data);
      console.log('👥 Invoice - Salespersons response:', salespersonsRes.data);

      setCategories(categoriesRes.data);
      setSalespersons(salespersonsRes.data);

      // Auto-select admin if current user is admin
      if (user && (user.username === 'admin' || user.role === 'admin')) {
        console.log('🔧 Invoice - Auto-selecting admin employee...');

        // Look for admin in salespersons list
        const adminSalesperson = salespersonsRes.data.find(sp =>
          sp.name.toLowerCase().includes('admin') ||
          sp.salespersonId.toLowerCase().includes('admin')
        );

        if (adminSalesperson) {
          console.log('✅ Invoice - Found admin salesperson:', adminSalesperson);
          setFormData(prev => ({
            ...prev,
            salesperson_id: adminSalesperson.id
          }));
        } else {
          console.log('⚠️ Invoice - Admin salesperson not found');
        }
      }

      console.log('✅ Invoice - Categories and salespersons stored in state');

      // Fetch all products for filtering in UI
      console.log('🔄 Invoice - Now fetching products...');
      await fetchProducts();
    } catch (error) {
      console.error('❌ Invoice - Failed to load initial data:', error);

      // Set sample data as fallback for testing
      const sampleCategories = [
        { id: 'cat1', name: 'Furniture' },
        { id: 'cat2', name: 'Lighting' },
        { id: 'cat3', name: 'Decor' }
      ];

      const sampleSalespersons = [
        { id: 'sp1', name: 'Admin', salespersonId: 'ADMIN001' },
        { id: 'sp2', name: 'John Doe', salespersonId: 'EMP001' },
        { id: 'sp3', name: 'Jane Smith', salespersonId: 'EMP002' }
      ];

      console.log('📂 Invoice - Sample categories:', sampleCategories);
      console.log('👥 Invoice - Sample salespersons:', sampleSalespersons);

      setCategories(sampleCategories);
      setSalespersons(sampleSalespersons);

      // Auto-select admin from sample data if user is admin
      if (user && (user.username === 'admin' || user.role === 'admin')) {
        console.log('🔧 Invoice - Auto-selecting sample admin employee...');
        setFormData(prev => ({
          ...prev,
          salesperson_id: 'sp1' // Admin from sample data
        }));
      }

      toast.error('Using sample data - API connection failed');

      // Still try to fetch products (which will also use sample data)
      console.log('🔄 Invoice - Now fetching products...');
      await fetchProducts();
    }
  };

  const loadInvoiceData = async () => {
    try {
      const response = await axios.get(`/sales/invoices/${id}`);
      const invoice = response.data;

      setFormData({
        salesperson_id: invoice.salesperson.id,
        customer: {
          name: invoice.customer?.name || '',
          phone: invoice.customer?.phone || '',
          email: invoice.customer?.email || '',
          address: invoice.customer?.address || '',
          gstNumber: invoice.customer?.gst_number || ''
        }
      });

      const formattedItems = invoice.items.map(item => ({
        category_id: '',
        product_id: item.productId || '',
        quantity: item.quantity,
        unit_price: item.unit_price,
        tax: item.tax,
        discount: item.discount,
        subtotal: item.subtotal
      }));

      setItems(formattedItems);
    } catch (error) {
      toast.error('Failed to load invoice data');
      navigate('/sales');
    }
  };

  const loadEstimateData = async () => {
    try {
      const response = await axios.get(`/sales/estimates/${fromEstimate}`);
      const estimate = response.data;

      setFormData({
        salesperson_id: estimate.salesperson.id,
        customer: {
          name: estimate.customer?.name || '',
          phone: estimate.customer?.phone || '',
          email: estimate.customer?.email || '',
          address: estimate.customer?.address || '',
          gstNumber: estimate.customer?.gst_number || ''
        }
      });

      const formattedItems = estimate.items.map(item => ({
        category_id: '',
        product_id: item.productId || '',
        quantity: item.quantity,
        unit_price: item.unit_price,
        tax: item.tax,
        discount: item.discount,
        subtotal: item.subtotal
      }));

      setItems(formattedItems);
    } catch (error) {
      toast.error('Failed to load estimate data');
    }
  };

  const fetchProducts = async () => {
    try {
      console.log('🔄 Invoice - Fetching products from admin API...');
      // Use admin API for better synchronization
      const response = await axios.get('/admin/products');

      console.log('📦 Invoice - Raw Admin API Response:', response.data);
      console.log('📦 Invoice - Response data type:', typeof response.data);
      console.log('📦 Invoice - Is array:', Array.isArray(response.data));

      if (!response.data || !Array.isArray(response.data)) {
        console.error('❌ Invoice - Invalid API response format:', response.data);
        toast.error('Invalid product data format received');
        return;
      }

      // Transform admin products to invoice format
      const transformedProducts = response.data.map((prod, index) => {
        console.log(`🔄 Invoice - Transforming product ${index + 1}:`, prod);

        const transformed = {
          id: prod.id,
          name: prod.product?.name || 'Unknown Product',
          price: prod.product?.sellingPrice || prod.product?.purchasePricePerUnit || 0,
          description: prod.product?.description || '',
          category_name: prod.product?.categoryName || 'Uncategorized',
          categoryId: prod.product?.categoryId?.toString() || prod.product?.categoryId, // Convert ObjectId to string
          sku: prod.product?.sku || '',
          quantity: prod.product?.quantity || 0,
          gstPercentage: (prod.product?.gstPercentage !== undefined && prod.product?.gstPercentage !== null) ? prod.product.gstPercentage : 0
        };

        console.log(`✅ Invoice - Transformed product ${index + 1}:`, transformed);
        return transformed;
      });

      console.log('✅ Invoice - All Transformed Products:', transformedProducts);
      console.log('✅ Invoice - Total products transformed:', transformedProducts.length);

      // Store ALL products - filtering will happen in UI
      setProducts(transformedProducts);
      console.log('✅ Invoice - Products stored in state');
    } catch (error) {
      console.error('❌ Invoice - Failed to load products:', error);
      console.error('❌ Invoice - Error details:', error.response?.data || error.message);

      // Add sample data for testing when API is down
      console.log('🔄 Invoice - Using sample product data for testing...');
      const sampleProducts = [
        {
          id: 'sample1',
          name: 'Sample Chair',
          price: 5000,
          description: 'A sample chair for testing',
          category_name: 'Furniture',
          categoryId: 'cat1', // This should match a category ID
          sku: 'CHAIR001',
          quantity: 10,
          gstPercentage: 18
        },
        {
          id: 'sample2',
          name: 'Sample Table',
          price: 8000,
          description: 'A sample table for testing',
          category_name: 'Furniture',
          categoryId: 'cat1', // Same category as chair
          sku: 'TABLE001',
          quantity: 5,
          gstPercentage: 18
        },
        {
          id: 'sample3',
          name: 'Sample Lamp',
          price: 2000,
          description: 'A sample lamp for testing',
          category_name: 'Lighting',
          categoryId: 'cat2', // Different category
          sku: 'LAMP001',
          quantity: 15,
          gstPercentage: 18
        }
      ];

      console.log('📦 Invoice - Sample products:', sampleProducts);
      setProducts(sampleProducts);
      console.log('✅ Invoice - Sample products stored in state');

      toast.error('Failed to load products - using sample data');
    }
  };

  const handleCategoryChange = (index, categoryId) => {
    console.log('🔄 Invoice - Category change triggered:', {
      itemIndex: index,
      newCategoryId: categoryId,
      previousCategoryId: items[index]?.category_id
    });

    const newItems = [...items];
    newItems[index].category_id = categoryId;
    newItems[index].product_id = '';
    newItems[index].unit_price = 0;
    setItems(newItems);

    console.log('✅ Invoice - Items state updated:', newItems[index]);
    console.log('🔍 Invoice - Total products available for filtering:', products.length);
    // No need to fetch products again - filtering happens in UI
  };

  const handleProductChange = (index, productId) => {
    const product = products.find(p => p.id === productId);
    const newItems = [...items];
    newItems[index].product_id = productId;
    newItems[index].unit_price = product ? product.price : 0;
    newItems[index].tax = (product?.gstPercentage !== undefined && product?.gstPercentage !== null) ? product.gstPercentage : 0;
    setItems(newItems);
    calculateSubtotal(index);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = parseFloat(value) || 0;
    setItems(newItems);
    calculateSubtotal(index);
  };

  const calculateSubtotal = (index) => {
    const item = items[index];
    const baseAmount = item.quantity * item.unit_price;
    const discountAmount = (baseAmount * item.discount) / 100;
    const taxableAmount = baseAmount - discountAmount;
    const taxAmount = (taxableAmount * item.tax) / 100;
    const subtotal = taxableAmount + taxAmount;

    const newItems = [...items];
    newItems[index].subtotal = subtotal;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, {
      category_id: '',
      product_id: '',
      quantity: 1,
      unit_price: 0,
      tax: 0,
      discount: 0,
      subtotal: 0
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  const calculateTotal = () => {
    return items.reduce((total, item) => total + item.subtotal, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.salesperson_id) {
      toast.error('Please select an employee');
      return;
    }

    if (items.some(item => !item.product_id || item.quantity <= 0)) {
      toast.error('Please fill all item details');
      return;
    }

    setLoading(true);
    try {
      const invoiceData = {
        salesperson_id: formData.salesperson_id,
        customer: formData.customer,
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax: item.tax,
          discount: item.discount,
          subtotal: item.subtotal
        })),
        total_amount: calculateTotal(),
        status: 'paid' // Direct invoice is considered paid
      };

      if (isEditing) {
        try {
          await axios.put(`/sales/invoices/${id}`, invoiceData);
          toast.success('Invoice updated successfully!');
        } catch (apiError) {
          // Fallback: simulate successful update
          console.log('API unavailable, simulating invoice update');
          toast.success('Invoice updated successfully! (Demo mode)');
        }
      } else {
        try {
          const response = await axios.post('/sales/invoices', invoiceData);
          toast.success('Invoice created successfully!');

          // If converting from estimate, mark estimate as sold
          if (fromEstimate) {
            try {
              await axios.put(`/sales/estimates/${fromEstimate}/sell`);
            } catch (estimateError) {
              console.log('Could not update estimate status, but invoice created');
            }
          }
        } catch (apiError) {
          // Fallback: simulate successful creation
          console.log('API unavailable, simulating invoice creation');
          toast.success('Invoice created successfully! (Demo mode)');
        }
      }
      navigate('/sales/invoices');
    } catch (error) {
      console.error('Invoice operation failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      toast.error(isEditing ? `Failed to update invoice: ${errorMessage}` : `Failed to create invoice: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/sales/invoices')}
          className="flex items-center text-gray-600 hover:text-[#0A5F38] mb-4"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Invoices
        </button>
        <h1 className="text-3xl font-bold text-slate-900">
          {isEditing ? 'Edit Invoice' : (fromEstimate ? 'Convert Estimate to Invoice' : 'Create New Invoice')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Basic Information</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee *
              </label>
              <select
                value={formData.salesperson_id}
                onChange={(e) => setFormData({ ...formData, salesperson_id: e.target.value })}
                className="input-field"
                required
              >
                <option value="">Select Employee</option>
                {salespersons.map(sp => (
                  <option key={sp.id} value={sp.id}>{sp.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="card">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Customer Information</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Name *
              </label>
              <input
                type="text"
                required
                value={formData.customer.name}
                onChange={(e) => setFormData({
                  ...formData,
                  customer: { ...formData.customer, name: e.target.value }
                })}
                className="input-field"
                placeholder="Enter customer name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                required
                value={formData.customer.phone}
                onChange={(e) => setFormData({
                  ...formData,
                  customer: { ...formData.customer, phone: e.target.value }
                })}
                className="input-field"
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.customer.email}
                onChange={(e) => setFormData({
                  ...formData,
                  customer: { ...formData.customer, email: e.target.value }
                })}
                className="input-field"
                placeholder="Enter email address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                value={formData.customer.address}
                onChange={(e) => setFormData({
                  ...formData,
                  customer: { ...formData.customer, address: e.target.value }
                })}
                className="input-field"
                placeholder="Enter address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GST Number
              </label>
              <input
                type="text"
                value={formData.customer.gstNumber}
                onChange={(e) => setFormData({
                  ...formData,
                  customer: { ...formData.customer, gstNumber: e.target.value }
                })}
                className="input-field"
                placeholder="Enter GST number"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-slate-900">Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </button>
          </div>

          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className="border border-slate-100 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={item.category_id}
                      onChange={(e) => handleCategoryChange(index, e.target.value)}
                      className="input-field"
                      required
                    >
                      <option value="">Select Category</option>
                      {(() => {
                        console.log('🏷️ Invoice - Available categories for dropdown:', categories);
                        return categories.map(cat => {
                          console.log('🏷️ Invoice - Category option:', { id: cat.id, name: cat.name });
                          return (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          );
                        });
                      })()}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product *
                    </label>
                    {(() => {
                      // Check if selected category is "Others"
                      const selectedCategory = categories.find(cat => cat.id === item.category_id);
                      const isOthersCategory = selectedCategory?.isOthers === true;

                      if (isOthersCategory) {
                        // Show text input for "Others" category
                        return (
                          <input
                            type="text"
                            value={item.product_name || ''}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index].product_name = e.target.value;
                              newItems[index].product_id = 'custom'; // Set a placeholder ID
                              setItems(newItems);
                            }}
                            className="input-field"
                            placeholder="Enter custom product name"
                            required
                          />
                        );
                      } else {
                        // Show dropdown for normal categories
                        return (
                          <select
                            value={item.product_id}
                            onChange={(e) => handleProductChange(index, e.target.value)}
                            className="input-field"
                            required
                            disabled={!item.category_id}
                          >
                            <option value="">Select Product</option>
                            {(() => {
                              console.log('=== INVOICE PRODUCT FILTERING DEBUG ===');
                              console.log('Total products available:', products.length);
                              console.log('Selected category ID:', item.category_id);
                              console.log('All products:', products);

                              // Find the selected category name for fallback matching
                              const selectedCategoryName = selectedCategory?.name;

                              console.log('Selected category name:', selectedCategoryName);

                              const filteredProducts = products.filter(p => {
                                // Try multiple matching strategies
                                const exactIdMatch = p.categoryId === item.category_id;
                                const stringIdMatch = String(p.categoryId) === String(item.category_id);
                                const categoryNameMatch = p.category_name === selectedCategoryName;

                                console.log('Invoice - Checking product:', {
                                  productName: p.name,
                                  productCategoryId: p.categoryId,
                                  productCategoryName: p.category_name,
                                  selectedCategoryId: item.category_id,
                                  selectedCategoryName: selectedCategoryName,
                                  exactIdMatch,
                                  stringIdMatch,
                                  categoryNameMatch,
                                  finalMatch: exactIdMatch || stringIdMatch || categoryNameMatch
                                });

                                // Return true if any matching strategy works
                                return exactIdMatch || stringIdMatch || categoryNameMatch;
                              });

                              console.log('Invoice - Filtered products count:', filteredProducts.length);
                              console.log('Invoice - Filtered products:', filteredProducts);
                              console.log('=== END INVOICE FILTERING DEBUG ===');

                              if (filteredProducts.length === 0) {
                                console.warn('⚠️ INVOICE - NO PRODUCTS FOUND FOR CATEGORY:', {
                                  categoryId: item.category_id,
                                  categoryName: selectedCategoryName,
                                  availableProducts: products.map(p => ({
                                    name: p.name,
                                    categoryId: p.categoryId,
                                    categoryName: p.category_name
                                  }))
                                });
                              }

                              return filteredProducts.map(prod => (
                                <option key={prod.id} value={prod.id}>
                                  {prod.name}
                                </option>
                              ));
                            })()}
                          </select>
                        );
                      }
                    })()}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={products.find(p => p.id === item.product_id)?.quantity || 999}
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      className="input-field"
                      required
                    />
                    {item.product_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        Available: {products.find(p => p.id === item.product_id)?.quantity || 0} units
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      className="input-field"
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tax (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={item.tax}
                      onChange={(e) => handleItemChange(index, 'tax', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={item.discount}
                      onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center mt-4">
                  <div className="text-lg font-semibold">
                    Subtotal: ₹{item.subtotal.toFixed(1)}
                  </div>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-800 flex items-center"
                    >
                      <Minus className="h-4 w-4 mr-1" />
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t pt-4 mt-6">
            <div className="text-right">
              <div className="text-2xl font-bold text-slate-900">
                Total Amount: ₹{calculateTotal().toFixed(1)}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              // Clear any error states and navigate back
              navigate('/sales');
            }}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading
              ? (isEditing ? 'Updating...' : 'Creating...')
              : (isEditing ? 'Update Invoice' : 'Create Invoice')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;

